import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import useOrderInfo from "@repo/shopify/hooks/useOrderInfo";
import useOrderStatus from "@repo/shopify/hooks/useOrderStatus";
import type { IOrdersEdgesNode } from "@repo/shopify/queries/customer-gql";
import { extractId } from "@repo/shopify/utils/common";
import { OrderStatusButtonType, OrderStatusTrans, getTrackingInfo } from "@repo/shopify/utils/order";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";

import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Price from "@atoms/Price";

import OrderStatusButton from "@organisms/OrderStatusButton";

import { URLS } from "@const/urls";

import useLanguage from "@hooks/useLanguage";

export default function OrderHistoryCard({ node }: { node: IOrdersEdgesNode }) {
  const { t } = useTranslation();
  const { isPC } = useCheckDevice();
  const { lang } = useLanguage();

  const { activeButton, orderStatus } = useOrderStatus(node, lang === "ko");

  // 주문 정보
  const { totalAmount } = useOrderInfo(node);

  // 배송 정보
  const trackingInfo = getTrackingInfo({ order: node, lang });

  return (
    <li className="tnt-gap-x pc:grid-cols-12 pc:gap-y-0 grid grid-cols-8 gap-y-2.5">
      <div className="pc:col-span-2 col-span-3 col-start-1">
        <Link as="wrapper" className="flex! flex-col" href={`${URLS.ORDER}${extractId(node.id)}`}>
          <span>{node.name}</span>
          <span>{dayjs(node.processedAt).format("MMM DD, YYYY")}</span>
          <span>{orderStatus ? t(OrderStatusTrans[orderStatus]) : orderStatus}</span>
        </Link>
        {trackingInfo ? (
          <Link as="text" className="w-fit font-normal underline" href={trackingInfo?.url}>
            {trackingInfo.number}
          </Link>
        ) : null}
      </div>
      {isPC && node?.lineItems.edges.length > 0 ? (
        <Link
          as="wrapper"
          className="pc:col-span-3 pc:col-start-3 col-span-3 col-start-3 flex flex-col"
          href={`${URLS.ORDER}${extractId(node.id)}`}
        >
          <ol>
            {node?.lineItems?.edges.map((item, index) => (
              <li key={`${node.id}-${index}-title`}>
                <span>{item.node.title}</span>
              </li>
            ))}
          </ol>
        </Link>
      ) : null}
      <Link as="wrapper" className="pc:col-span-1 col-span-3 block" href={`${URLS.ORDER}${extractId(node.id)}`}>
        <Price className="" price={totalAmount} />
      </Link>
      <Link
        as="wrapper"
        className="pc:col-span-4 pc:col-start-7 pc:row-start-1 col-span-8 row-start-2 block"
        href={`${URLS.ORDER}${extractId(node.id)}`}
      >
        <ol className="flex flex-wrap gap-1">
          {node?.lineItems?.edges.map((item, index) => (
            <li key={`${node.id}-${index}-thumbnail`}>
              <Image
                className="pc:w-20 pc:max-w-20 w-15 max-w-15"
                height={item.node.variant?.image?.height || 1707}
                src={item.node.variant?.image?.url || node.featuredImage?.value || ""}
                width={item.node.variant?.image?.width || 1280}
              />
            </li>
          ))}
        </ol>
      </Link>
      <div className="pc:col-start-11 col-span-2 col-start-7">
        <div className="pc:flex-row pc:gap-2 float-right flex flex-col-reverse items-end gap-1">
          <Link as="text" href={`${URLS.ORDER}${extractId(node.id)}`}>
            {t("view_detail")}
          </Link>
          {activeButton !== OrderStatusButtonType.NONE ? (
            <OrderStatusButton buttonType={activeButton} orderId={extractId(node.id)} />
          ) : null}
        </div>
      </div>
    </li>
  );
}
